/* eslint-disable no-param-reassign */
import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { AppThunk } from 'store';
import { FormValues } from 'components/Forms';
import CapacityType, { EditCapacityTypeRequest } from 'models/CapacityType';
import { DomainItem } from 'models/Domain';
import {
  StateSlice,
  initialStateSlice,
  startLoading,
  loadingFailed
} from 'store/slices';
import { capacityTypeDomain } from 'models';
import http from 'api/http';
import { handleThunkError } from 'store/utils/errors';
import {
  fetchItemThunk,
  createUrl,
  addItemThunkNew,
  editItemThunkNew
} from 'store/utils/';
import { setSnackbar, SnackbarStatus } from './snackbar';

export interface CapacityTypesStateSlice extends StateSlice<CapacityType> {
  allItems?: CapacityType[];
}

const initialCapacityTypesStateSlice: CapacityTypesStateSlice = {
  ...initialStateSlice,
  allItems: []
};

const sortByCapacityTypeName = (a: CapacityType, b: CapacityType) =>
  a.name.localeCompare(b.name);

const capacityTypes = createSlice({
  name: capacityTypeDomain.plural,
  initialState: initialCapacityTypesStateSlice,
  reducers: {
    ...startLoading<CapacityType>(),
    ...loadingFailed<CapacityType>(),
    getCapacityTypesSuccess(
      state: CapacityTypesStateSlice,
      { payload: items }: PayloadAction<CapacityType[]>
    ) {
      state.items = [...items].sort(sortByCapacityTypeName);
      state.isLoading = false;
      state.error = null;
    },
    getCapacityTypeSuccess(
      state: CapacityTypesStateSlice,
      { payload: capacityTypeItem }: PayloadAction<CapacityType>
    ) {
      if (state.items != null) {
        state.items = state.items.filter(
          item => item.id !== capacityTypeItem.id
        );
        state.items.push(capacityTypeItem);
        state.items.sort(sortByCapacityTypeName);
      }
      state.isLoading = false;
      state.error = null;
    },
    addCapacityTypeSuccess(state: CapacityTypesStateSlice) {
      state.isLoading = false;
      state.error = null;
    },
    editCapacityTypeSuccess(state: CapacityTypesStateSlice) {
      state.isLoading = false;
      state.error = null;
    },
    deleteCapacityTypeSuccess(
      state: CapacityTypesStateSlice,
      { payload: deletedCapacityTypeId }: PayloadAction<string>
    ) {
      if (state.items != null) {
        state.items = state.items.filter(
          item => item.id !== deletedCapacityTypeId
        );
      }

      state.isLoading = false;
      state.error = null;
    }
  }
});

export const {
  requestStart,
  requestFail,
  getCapacityTypesSuccess,
  addCapacityTypeSuccess,
  editCapacityTypeSuccess,
  deleteCapacityTypeSuccess,
  getCapacityTypeSuccess
} = capacityTypes.actions;

export default capacityTypes.reducer;

export function fetchCapacityTypes(includeDeletedItems = false): AppThunk {
  return async dispatch => {
    const url = createUrl(capacityTypeDomain);
    try {
      dispatch(requestStart());

      const headers = includeDeletedItems
        ? {
            headers: { 'X-Include-Deleted-Records': true }
          }
        : undefined;

      const { data } = await http.get<CapacityType[]>(url, headers);

      dispatch(getCapacityTypesSuccess(data));
    } catch (error) {
      handleThunkError({
        error,
        requestFail,
        dispatch,
        domainType: capacityTypeDomain.type,
        useNewSnackbar: true
      });
    }
  };
}

export function fetchCapacityType(id: string): AppThunk {
  return fetchItemThunk<CapacityType>(
    capacityTypeDomain,
    requestStart,
    requestFail,
    getCapacityTypeSuccess,
    id
  );
}

export function addCapacityType(item: FormValues): AppThunk | any {
  return addItemThunkNew({
    domain: capacityTypeDomain,
    requestStart,
    requestFail,
    requestSuccess: addCapacityTypeSuccess,
    item,
    successMessage: `${capacityTypeDomain.label} successfully added.`
  });
}

export function editCapacityType(item: EditCapacityTypeRequest): AppThunk {
  return editItemThunkNew({
    domain: capacityTypeDomain,
    requestStart,
    requestFail,
    requestSuccess: editCapacityTypeSuccess,
    item
  });
}

export function deleteCapacityType(
  domainItem: DomainItem,
  onCapacityTypeInUse: (errorMessage: string) => void
): AppThunk {
  return async dispatch => {
    const url = createUrl(capacityTypeDomain, undefined, domainItem.id);

    try {
      await http.delete<DomainItem>(url);

      dispatch(deleteCapacityTypeSuccess(domainItem.id));
      dispatch(
        setSnackbar({
          message: `${capacityTypeDomain.label} successfully deleted.`,
          show: true,
          status: SnackbarStatus.Success,
          useNewSnackbar: true
        })
      );
    } catch (error) {
      const isCapacityTypeInUse = error.response?.status === 409;

      if (isCapacityTypeInUse) {
        onCapacityTypeInUse(error.response?.data.message);
      }

      handleThunkError({
        error,
        requestFail,
        displayError: !isCapacityTypeInUse,
        dispatch,
        domainType: capacityTypeDomain.type,
        useNewSnackbar: true
      });
    }
  };
}
